import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'

const ExperienceNavContext = createContext(null)

// Module-level counter, not component state: registrations are identified
// across the whole app lifetime, not per-provider, so two ExperienceNavBack
// instances mounted at the same moment never collide on id.
let nextRegistrationId = 0

const upsertRegistration = (registration) => (current) => {
  const existingIndex = current.findIndex((entry) => entry.id === registration.id)
  if (existingIndex === -1) return [...current, registration]

  const updated = [...current]
  updated[existingIndex] = registration
  return updated
}

const removeRegistrationById = (registrationId) => (current) => current.filter((entry) => entry.id !== registrationId)

// Backs the floating ExperienceNavControls (rendered by ConditionalHeader)
// with whichever in-context screen most recently registered a Back
// destination. DsApp mounts one app-level provider so contextual
// ExperienceNavBack registrations reach the controls whether an experience
// uses ExperienceShell, ConditionalHeader directly, or another composition.
export function ExperienceNavProvider({ children }) {
  const [registrations, setRegistrations] = useState([])
  // Section rows declared from inside an experience rather than passed down as a prop.
  // An experience whose rows depend on data it fetches cannot build them at the shell,
  // which sits above whatever holds that data - so it declares them from where it knows.
  const [itemRegistrations, setItemRegistrations] = useState([])

  // Whether ExperienceNavControls actually rendered anything, published by
  // the controls themselves (they alone know the full predicate: mode,
  // kiosk type, registration, Home visibility, returnTo). Experiences that
  // lay out around the controls (e.g. heritage's country band reserving room
  // beside them) read this instead of hand-mirroring the predicate.
  const [controlsRendered, setControlsRendered] = useState(false)
  // And whether what rendered sits in the top-start corner (WEB's round
  // trigger) rather than centred against the side (STATION's edge tab). Content
  // anchored at the top reads this one: a rendered edge tab is clear of it and
  // needs no room reserved.
  const [occupiesTopCorner, setOccupiesTopCorner] = useState(false)

  const unregister = useCallback((registrationId) => {
    setRegistrations(removeRegistrationById(registrationId))
  }, [])

  const unregisterNavItems = useCallback((registrationId) => {
    setItemRegistrations(removeRegistrationById(registrationId))
  }, [])

  const registerNavItems = useCallback(
    (registration) => {
      setItemRegistrations(upsertRegistration(registration))
      return () => unregisterNavItems(registration.id)
    },
    [unregisterNavItems]
  )

  // Stable across the provider's lifetime (setRegistrations is stable and
  // the updater form doesn't close over `registrations`) so ExperienceNavBack
  // effects that depend on it don't re-fire just because some other
  // registration mounted/unmounted.
  const register = useCallback(
    (registration) => {
      setRegistrations(upsertRegistration(registration))
      return () => unregister(registration.id)
    },
    [unregister]
  )

  // Newest registration wins - the most-recently-mounted (deepest) screen's
  // Back destination is the one the floating controls should show. Spelled
  // out rather than Array.prototype.findLast (ES2023): the build ships no
  // polyfill for it, and this runs at the app root on every render.
  const back = [...registrations].reverse().find((registration) => registration.active !== false) ?? null
  // Newest wins, as with Back: the deepest mounted screen describes the level in view.
  const navItems = itemRegistrations.at(-1) ?? null

  const beforeCloseHandlersRef = useRef([])

  const registerBeforeClose = useCallback((id, handlerRef) => {
    beforeCloseHandlersRef.current = [...beforeCloseHandlersRef.current, { id, handlerRef }]
    return () => {
      beforeCloseHandlersRef.current = beforeCloseHandlersRef.current.filter((entry) => entry.id !== id)
    }
  }, [])

  // A throwing handler must not stop the other handlers or abort the close.
  const runBeforeClose = useCallback(() => {
    beforeCloseHandlersRef.current.forEach(({ handlerRef }) => {
      try {
        handlerRef.current?.()
      } catch (err) {
        console.error('ExperienceNavContext: a before-close handler threw', err)
      }
    })
  }, [])

  const value = useMemo(
    () => ({
      back,
      navItems,
      register,
      unregister,
      registerNavItems,
      unregisterNavItems,
      controlsRendered,
      setControlsRendered,
      occupiesTopCorner,
      setOccupiesTopCorner,
      registerBeforeClose,
      runBeforeClose,
    }),
    // Both sides of the merge added members: the nav-item registration from DSC-8008, the
    // before-close handlers from master. The value object carries both, so the deps do too.
    [
      back,
      navItems,
      register,
      unregister,
      registerNavItems,
      unregisterNavItems,
      controlsRendered,
      occupiesTopCorner,
      registerBeforeClose,
      runBeforeClose,
    ]
  )

  return <ExperienceNavContext.Provider value={value}>{children}</ExperienceNavContext.Provider>
}

ExperienceNavProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

/**
 * Registers an in-context Back destination with the floating
 * ExperienceNavControls. Renders nothing - the controls own the visible
 * button. Provide `to` (a path the controls will push) and/or `onClick` (a
 * custom handler, used instead of pushing `to` when both are given) - with
 * neither, nothing registers (rather than putting a Back control on screen whose
 * click would push null into the router).
 *
 * Pass `label` when the destination has a name worth showing ("Choose an
 * Ancestor"): the drawer then renders it as the card's top "level above" row
 * instead of a generic "Back". Leave it off and the generic row is used, which
 * is the right call when the destination is just "wherever you came from".
 * An experience that knows its whole level structure up front supplies the
 * same row app-side with ExperienceShell's `navParent` - that wins over a
 * registration, since it is the more complete description.
 *
 * @example
 * <ExperienceNavBack onClick={handleClose} ariaLabel="Back to all name activities" />
 */
export function ExperienceNavBack({
  to = null,
  onClick = null,
  label = null,
  ariaLabel = null,
  analyticsEventName = null,
}) {
  const nav = useContext(ExperienceNavContext)
  const register = nav?.register
  const unregister = nav?.unregister
  const registrationId = useRef(null)

  if (registrationId.current === null) {
    registrationId.current = nextRegistrationId++
  }

  // useLayoutEffect (not useEffect) so the controls re-render with the new
  // Back registration before paint - otherwise the previous screen's control
  // row (or none at all) would flash for a frame during navigation.
  useLayoutEffect(() => {
    if (!register || !unregister) return
    register({
      id: registrationId.current,
      active: Boolean(to || onClick),
      to,
      onClick,
      label,
      ariaLabel,
      analyticsEventName,
    })
  }, [register, unregister, to, onClick, label, ariaLabel, analyticsEventName])

  useLayoutEffect(() => {
    return () => unregister?.(registrationId.current)
  }, [unregister])

  return null
}

/**
 * Declares the card's section rows from inside an experience, for the case the prop on
 * ExperienceShell cannot serve: rows that depend on data fetched below the shell. Rendering
 * this overrides the navItems/navParent props for as long as it is mounted; an experience
 * that passes them as props is untouched by it.
 */
export function ExperienceNavItems({ items = null, parent = null }) {
  const nav = useContext(ExperienceNavContext)
  const registerNavItems = nav?.registerNavItems
  const unregisterNavItems = nav?.unregisterNavItems
  const registrationId = useRef(null)

  if (registrationId.current === null) {
    registrationId.current = nextRegistrationId++
  }

  // Layout effect for the same reason as ExperienceNavBack's: the controls must re-render
  // with the new rows before paint, or the previous screen's index flashes for a frame.
  useLayoutEffect(() => {
    if (!registerNavItems) return
    registerNavItems({ id: registrationId.current, items, parent })
  }, [registerNavItems, items, parent])

  useLayoutEffect(() => {
    return () => unregisterNavItems?.(registrationId.current)
  }, [unregisterNavItems])

  return null
}

ExperienceNavItems.propTypes = {
  /** The section rows, already filtered to whatever the experience wants offered. */
  items: PropTypes.arrayOf(PropTypes.shape({})),
  /** The level above them - the card's top row. */
  parent: PropTypes.shape({}),
}

ExperienceNavBack.propTypes = {
  /** Path pushed by the controls when clicked. Provide this and/or `onClick`. */
  to: PropTypes.string,
  /** Custom click handler; used instead of pushing `to` when both are given. */
  onClick: PropTypes.func,
  /** Names the destination, promoting the generic Back row to a "level above" row. */
  label: PropTypes.string,
  /** Overrides the controls' default generic "Back" aria-label. */
  ariaLabel: PropTypes.string,
  /** Overrides the controls' default analytics event name fired on click. */
  analyticsEventName: PropTypes.string,
}

// Returns the nearest ExperienceNavProvider's value, or null when rendered
// without one (e.g. a component reused outside the shell).
export function useExperienceNav() {
  return useContext(ExperienceNavContext)
}

/**
 * Registers `handler` to run before the ExperienceNavControls close action
 * closes the activity. The latest handler is always used; no memoization needed.
 */
export function useBeforeExperienceClose(handler) {
  const registerBeforeClose = useContext(ExperienceNavContext)?.registerBeforeClose
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useLayoutEffect(() => {
    if (!registerBeforeClose) return undefined
    const id = nextRegistrationId++
    return registerBeforeClose(id, handlerRef)
  }, [registerBeforeClose])
}

/**
 * Whether the floating ExperienceNavControls rendered anything on this
 * screen. False until the controls' pre-paint publish, and false without a
 * provider. Use this - never a re-derived predicate - when content must lay
 * out around the controls.
 */
export function useExperienceNavRendered() {
  return Boolean(useContext(ExperienceNavContext)?.controlsRendered)
}

/**
 * Whether the rendered controls occupy the top-start corner: WEB's round
 * trigger does, STATION's edge tab (centred against the side) does not. False
 * until the controls publish, and false without a provider. Top-anchored
 * content that must clear the controls reads this rather than
 * useExperienceNavRendered, which is true for the edge tab too.
 */
export function useExperienceNavOccupiesTopCorner() {
  return Boolean(useContext(ExperienceNavContext)?.occupiesTopCorner)
}
